import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Upload, X, User } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useUpload } from "@/lib/upload";
import { fileUrl } from "@/lib/format";
import type { NewTeamMember, TeamMember } from "@shared/schema";

/**
 * Pulls a human-readable, field-level message out of a failed save. The backend
 * returns the Zod validation list as `{ error, issues: [...] }`; we surface the
 * first issue's (Hungarian) message instead of the raw `invalid_input` code.
 */
function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body;
    if (body && typeof body === "object" && "issues" in body) {
      const issues = (body as { issues?: unknown }).issues;
      if (Array.isArray(issues) && issues.length > 0) {
        const first = issues[0] as { message?: unknown };
        if (typeof first?.message === "string" && first.message.trim()) {
          return first.message;
        }
      }
    }
  }
  return "Mentési hiba";
}

export function AdminTeam() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "team"],
    queryFn: () => api<TeamMember[]>("/api/admin/team"),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/team/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "team"] });
      qc.invalidateQueries({ queryKey: ["team"] });
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Csapattagok</h1>
        <button
          type="button"
          onClick={() => {
            setCreating(true);
            setEditing(null);
          }}
          className="btn-primary"
        >
          <Plus size={16} /> Új csapattag
        </button>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="card overflow-hidden">
          {q.isLoading && <div className="p-6 text-brand-500">Betöltés...</div>}
          {q.data?.length === 0 && (
            <div className="p-6 text-brand-500">Még nincs csapattag.</div>
          )}
          <ul className="divide-y divide-brand-100">
            {q.data?.map((m) => (
              <li
                key={m.id}
                className={`p-4 flex items-center gap-4 ${
                  editing?.id === m.id ? "bg-brand-50" : ""
                }`}
              >
                {m.photoR2Key ? (
                  <img
                    src={fileUrl(m.photoR2Key)}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand-100 grid place-items-center text-brand-400">
                    <User size={20} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEditing(m);
                    setCreating(false);
                  }}
                  className="flex-1 text-left"
                >
                  <div className="font-medium text-sm">
                    {m.name}{" "}
                    {!m.isActive && (
                      <span className="ml-2 text-xs bg-brand-100 text-brand-500 px-1.5 py-0.5 rounded">
                        inaktív
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-brand-500 mt-0.5">{m.role}</div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Törli: ${m.name}?`)) del.mutate(m.id);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {(editing || creating) && (
            <MemberEditor
              key={editing?.id ?? "new"}
              member={editing}
              onSaved={() => {
                setEditing(null);
                setCreating(false);
                qc.invalidateQueries({ queryKey: ["admin", "team"] });
                qc.invalidateQueries({ queryKey: ["team"] });
              }}
              onCancel={() => {
                setEditing(null);
                setCreating(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function MemberEditor({
  member,
  onSaved,
  onCancel,
}: {
  member: TeamMember | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const isNew = !member;
  const [draft, setDraft] = useState<Partial<NewTeamMember>>({
    name: member?.name ?? "",
    role: member?.role ?? "",
    email: member?.email ?? "",
    phone: member?.phone ?? "",
    photoR2Key: member?.photoR2Key ?? null,
    sortOrder: member?.sortOrder ?? 0,
    isActive: member?.isActive ?? true,
  });
  const { uploading, uploadError, uploadSuccess, run } = useUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: () => {
      // Optional fields: send null (not an empty string) when blank so the DB
      // stores null and the email format check is skipped for an empty value.
      const payload = {
        ...draft,
        email: draft.email?.trim() || null,
        phone: draft.phone?.trim() || null,
      };
      return isNew
        ? api("/api/admin/team", { method: "POST", body: payload })
        : api(`/api/admin/team/${member!.id}`, {
            method: "PATCH",
            body: payload,
          });
    },
    onSuccess: onSaved,
  });

  function uploadPhoto(file: File) {
    run(async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "team");
      const res = await api<{ key: string }>("/api/admin/upload", {
        method: "POST",
        formData: fd,
      });
      setDraft({ ...draft, photoR2Key: res.key });
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="card p-6 space-y-4"
    >
      <h3 className="font-semibold">
        {isNew ? "Új csapattag" : draft.name || "Csapattag szerkesztése"}
      </h3>

      <div>
        <label className="label">Fotó</label>
        <div className="flex items-center gap-4">
          {draft.photoR2Key ? (
            <div className="relative">
              <img
                src={fileUrl(draft.photoR2Key)}
                alt=""
                className="w-16 h-16 rounded-full object-cover"
              />
              <button
                type="button"
                onClick={() => setDraft({ ...draft, photoR2Key: null })}
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600"
              >
                <X size={11} />
              </button>
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-brand-100 grid place-items-center text-brand-400">
              <User size={24} />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadPhoto(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary text-xs"
          >
            <Upload size={14} />
            {uploading ? "Feltöltés..." : "Fotó feltöltése"}
          </button>
        </div>
        {uploadError && (
          <p className="mt-1 text-xs text-red-600">{uploadError}</p>
        )}
        {uploadSuccess && (
          <p className="mt-1 text-xs text-green-600">Feltöltve ✓</p>
        )}
      </div>

      <div>
        <label className="label">Név *</label>
        <input
          required
          className="input"
          value={draft.name ?? ""}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Pozíció *</label>
        <input
          required
          className="input"
          value={draft.role ?? ""}
          onChange={(e) => setDraft({ ...draft, role: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Email</label>
        <input
          type="email"
          className="input"
          value={draft.email ?? ""}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Telefon</label>
        <input
          type="tel"
          className="input"
          value={draft.phone ?? ""}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Sorrend</label>
          <input
            type="number"
            className="input"
            value={draft.sortOrder ?? 0}
            onChange={(e) =>
              setDraft({ ...draft, sortOrder: Number(e.target.value) })
            }
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm pb-3">
            <input
              type="checkbox"
              checked={draft.isActive ?? true}
              onChange={(e) =>
                setDraft({ ...draft, isActive: e.target.checked })
              }
            />
            Aktív
          </label>
        </div>
      </div>

      {save.isError && (
        <div className="text-sm text-red-600">
          {saveErrorMessage(save.error)}
        </div>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={save.isPending} className="btn-primary">
          {save.isPending ? "Mentés..." : "Mentés"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Mégse
        </button>
      </div>
    </form>
  );
}
